import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { trackingApi } from '../api/tracking';
import { ApiError } from '../api/client';
import { SectionHeader } from './SectionHeader';
import { DataTable } from './DataTable';
import { AuditLogEntry } from '../types/admin';

export function summarizeValues(value: unknown): string {
  if (value === null || value === undefined) return '-';
  // MySQL JSON columns come back from mysql2 already parsed into a real
  // object/array, not a JSON string - the previous version assumed a
  // string and called JSON.parse() on an object, which throws; the catch
  // block then returned the raw object itself, which React can't render
  // ("Objects are not valid as a React child"). This crashed on every
  // page with a real 'created' or 'updated' audit entry - i.e. almost
  // everywhere, since AuditHistoryTab is now wired onto nearly every page.
  let obj: unknown = value;
  if (typeof value === 'string') {
    try { obj = JSON.parse(value); } catch { return value; }
  }
  if (obj && typeof obj === 'object') {
    const parts = Object.entries(obj as Record<string, unknown>).map(([k, v]) => `${k}: ${v === null || v === undefined ? '-' : String(v)}`);
    return parts.length > 0 ? parts.join(', ') : '-';
  }
  return String(obj);
}

interface AuditHistoryTabProps {
  /** Pass 'mine' for the My Profile / self-service case, which uses a dedicated /employees/me/audit route instead. */
  entityType: string;
  entityId?: number;
}

/**
 * Generic per-record "Tracking" tab - same AuditEngine data the Admin
 * console's AuditPage already shows, scoped to one record and reachable
 * by any user who can already view that record (see audit.controller.ts).
 * A 403 here means the viewer's permission grant is scoped narrower than
 * "all" (project/department) - a disclosed limitation, not a bug in this
 * component - so it's shown as a quiet message rather than an error toast.
 */
export function AuditHistoryTab({ entityType, entityId }: AuditHistoryTabProps) {
  const isMine = entityType === 'mine';
  const query = useQuery({
    queryKey: ['tracking', entityType, entityId],
    queryFn: () => (isMine ? trackingApi.mine() : trackingApi.forEntity(entityType, entityId as number)),
    enabled: isMine || entityId !== undefined,
  });

  const entries = useMemo<AuditLogEntry[]>(() => query.data?.data ?? [], [query.data]);
  const forbidden = query.error instanceof ApiError && query.error.status === 403;

  return (
    <div className="panel">
      <SectionHeader title="Tracking" />
      <div className="panel-body">
        {query.isLoading && <p className="muted">Loading tracking history…</p>}
        {forbidden && <p className="muted">You do not have permission to view tracking for this record.</p>}
        {!query.isLoading && !forbidden && entries.length === 0 && <p className="muted">No tracking history yet.</p>}
        {!query.isLoading && !forbidden && entries.length > 0 && (
          <DataTable<AuditLogEntry>
            rows={entries}
            rowKey={(e) => e.id}
            columns={[
              { header: 'When', render: (e) => new Date(e.created_at).toLocaleString() },
              { header: 'Actor', render: (e) => e.actor_name ?? 'System' },
              { header: 'Action', render: (e) => e.action.replace(/_/g, ' ') },
              { header: 'Changed from', render: (e) => summarizeValues(e.old_values) },
              { header: 'Changed to', render: (e) => summarizeValues(e.new_values) },
            ]}
          />
        )}
      </div>
    </div>
  );
}
